/**
 * NavMenuSection — full navigation bar renderer for the Page Builder.
 * Reads MenuConfig from section_config and renders a complete, interactive nav.
 *
 * Features:
 *  - Horizontal / vertical / hamburger / tabs / pills layouts
 *  - Sticky-top / fixed-top / fixed-bottom / static positioning
 *  - Logo (text | image | both) with configurable link
 *  - Menu items: section-scroll, page-link, external URL, parent-label
 *  - Dropdown menus for items with children
 *  - Mobile hamburger drawer / overlay
 *  - Scroll spy (IntersectionObserver) with active highlight
 *  - Smooth scroll with per-item offset
 *  - Frosted glass backdrop-filter
 *  - Hide-on-scroll (detects scroll direction)
 *  - Scrolled-state background change
 *  - CTA button
 *  - Active indicator: underline | dot | background | border-left
 */

import { pageHref } from '../../../utils/pageHref';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import envConfig from '../../../config/envConfig';

// ── Defaults ──────────────────────────────────────────────────────────────────

const D = {
  menuLayout: 'horizontal',
  menuPosition: 'sticky-top',
  alignment: 'right',
  mobileBreakpoint: 768,
  mobileMenuStyle: 'hamburger-drawer',
  mobileDrawerSide: 'right',
  collapseOnItemClick: true,
  hideOnScroll: false,
  hideOnScrollThreshold: 100,
  stickyOffset: 0,

  logoVisible: true,
  logoType: 'text',
  logoText: 'My App',
  logoFontSize: 20,
  logoFontWeight: 700,
  logoColor: '',
  logoWidth: 120,
  logoHeight: 40,
  logoLinkNavType: 'url',
  logoLinkTarget: '/',

  navCtaVisible: false,
  navCtaLabel: 'Get Started',
  navCtaNavType: 'url',
  navCtaTarget: '/sign-up',
  navCtaStyle: 'filled',
  navCtaColor: '',
  navCtaTextColor: '#ffffff',
  navCtaBorderRadius: 6,
  navCtaSize: 'md',
  navCtaOpenNewTab: false,

  fontFamily: '',
  fontSize: 14,
  fontWeight: 500,
  labelColor: '#1e293b',
  labelColorHover: '',
  labelColorActive: '',
  activeIndicator: 'underline',
  activeIndicatorColor: '',
  activeIndicatorThickness: 2,
  itemSpacing: 8,
  itemPaddingX: 12,
  itemPaddingY: 8,
  itemBorderRadius: 4,
  itemHoverBgColor: '',
  itemActiveBgColor: '',

  navBgColor: '#ffffff',
  navBgOpacity: 100,
  navBgBlur: false,
  navBgBlurStrength: 8,
  navBorderBottom: true,
  navBorderColor: '#e2e8f0',
  navBorderWidth: 1,
  navHeight: 64,
  navPaddingX: 24,
  navShadow: 'sm',

  scrolledBgColor: '#ffffff',
  scrolledBgOpacity: 95,
  scrolledTriggerOffset: 50,

  smoothScrollDuration: 600,
  scrollEasing: 'ease-in-out',
  activeClass: 'active',

  scrollSpyEnabled: true,
  scrollSpyOffset: 80,
  scrollSpyThreshold: 0.3,
  hashUpdateOnScroll: false,

  dropdownBgColor: '#ffffff',
  dropdownBorderColor: '#e2e8f0',
  dropdownBorderRadius: 8,
  dropdownShadow: 'md',
  dropdownItemColor: '#374151',
  dropdownItemHoverBgColor: '#f1f5f9',
  dropdownItemPaddingX: 16,
  dropdownItemPaddingY: 8,
  dropdownOffset: 4,
  dropdownAnimation: 'fade',
  dropdownTrigger: 'hover',

  mobileMenuBgColor: '#1e293b',
  mobileMenuTextColor: '#f1f5f9',
  mobileMenuOverlayColor: '#000000',
  mobileMenuOverlayOpacity: 50,
  hamburgerIconColor: '#1e293b',
  hamburgerIconSize: 24,
  mobileMenuWidth: 280,

  transitionDuration: 200,
  customCss: '',
  items: [],
};

// ── Helpers ───────────────────────────────────────────────────────────────────

// Page Builder lets any menu item or CTA button link to an arbitrary URL,
// including the hardcoded /sign-in and /sign-up auth routes. Those routes
// already 404 when disabled via envConfig — so a link pointing at a
// disabled route must not render either, or it dead-ends the visitor.
function isDisabledAuthLink(navType, target) {
  if (navType !== 'url') return false;
  const path = String(target || '').replace(/\/+$/, '') || '/';
  if (path === '/sign-in') return !envConfig.isUserLoginAllowed;
  if (path === '/sign-up') return !envConfig.isUserRegistrationAllowed;
  return false;
}

function shadowStyle(shadow, color) {
  if (shadow === 'sm') return `0 1px 4px ${color || 'rgba(0,0,0,0.07)'}`;
  if (shadow === 'md') return `0 4px 16px ${color || 'rgba(0,0,0,0.10)'}`;
  if (shadow === 'lg') return `0 8px 32px ${color || 'rgba(0,0,0,0.14)'}`;
  return 'none';
}

function ctaPadding(size) {
  if (size === 'sm') return '4px 12px';
  if (size === 'lg') return '10px 22px';
  return '6px 16px';
}

const EASING_FN = {
  'linear':      t => t,
  'ease-in':     t => t * t,
  'ease-out':    t => t * (2 - t),
  'ease-in-out': t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
  'ease':        t => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
};

function smoothScrollTo(top, duration = 600, easing = 'ease-in-out') {
  const fn = EASING_FN[easing] || EASING_FN['ease-in-out'];
  const start = window.scrollY;
  const dist = top - start;
  const startTime = performance.now();
  const step = (now) => {
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / duration, 1);
    window.scrollTo(0, start + dist * fn(progress));
    if (progress < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function DropdownMenu({ item, cfg, onItemClick, activeId }) {
  const children = item.children || [];
  if (children.length === 0) return null;

  const style = {
    position: 'absolute',
    top: `calc(100% + ${cfg.dropdownOffset || 4}px)`,
    left: 0,
    minWidth: 180,
    background: cfg.dropdownBgColor || '#ffffff',
    border: `1px solid ${cfg.dropdownBorderColor || '#e2e8f0'}`,
    borderRadius: cfg.dropdownBorderRadius || 8,
    boxShadow: shadowStyle(cfg.dropdownShadow || 'md'),
    zIndex: 1000,
    overflow: 'hidden',
    animation: cfg.dropdownAnimation === 'fade' ? 'fadeIn 0.15s ease' :
               cfg.dropdownAnimation === 'slide-down' ? 'slideDown 0.2s ease' : 'none',
  };

  return (
    <div style={style}>
      {children.map(child => (
        <div key={child.itemId}
          onClick={e => onItemClick(child, e)}
          style={{
            padding: `${cfg.dropdownItemPaddingY || 8}px ${cfg.dropdownItemPaddingX || 16}px`,
            fontSize: (cfg.fontSize || 14) - 1,
            color: child.labelColor || cfg.dropdownItemColor || '#374151',
            cursor: child.disabled ? 'default' : 'pointer',
            opacity: child.disabled ? 0.5 : 1,
            display: 'flex', alignItems: 'center', gap: 8,
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = cfg.dropdownItemHoverBgColor || '#f1f5f9'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          {child.label}
          {child.navType === 'page' && child.targetPageType === 'private' && (
            <span style={{ fontSize: 10, color: '#94a3b8' }}>🔒</span>
          )}
          {child.navType === 'url' && child.isExternal && (
            <span style={{ fontSize: 10, color: '#94a3b8' }}>↗</span>
          )}
        </div>
      ))}
    </div>
  );
}

function NavItem({ item, cfg, isActive, openDropdownId, setOpenDropdownId, onItemClick, isMobile }) {
  const hasChildren = (item.children || []).length > 0;
  const isOpen = openDropdownId === item.itemId;

  const activePrimary = cfg.activeIndicatorColor || 'var(--primary-color)';
  const transMs = cfg.transitionDuration || 200;

  const indicatorStyle = (() => {
    if (!isActive) return {};
    switch (cfg.activeIndicator) {
      case 'underline':    return { borderBottom: `${cfg.activeIndicatorThickness || 2}px solid ${activePrimary}`, paddingBottom: Math.max((cfg.itemPaddingY || 8) - 2, 0) };
      case 'dot':          return { position: 'relative' };
      case 'background':   return { background: cfg.itemActiveBgColor || `${activePrimary}15`, borderRadius: cfg.itemBorderRadius || 4 };
      case 'border-left':  return { borderLeft: `${cfg.activeIndicatorThickness || 2}px solid ${activePrimary}`, paddingLeft: Math.max((cfg.itemPaddingX || 12) - 2, 0) };
      default:             return {};
    }
  })();

  const baseWeight = item.fontWeight || cfg.fontWeight || 500;
  const itemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: `${cfg.itemPaddingY || 8}px ${cfg.itemPaddingX || 12}px`,
    fontSize: item.fontSize || cfg.fontSize || 14,
    fontWeight: isActive ? Math.max(baseWeight, 600) : baseWeight,
    fontFamily: item.fontFamily || cfg.fontFamily || 'inherit',
    color: isActive ? (cfg.labelColorActive || cfg.labelColor || '#1e293b') : (item.labelColor || cfg.labelColor || '#1e293b'),
    letterSpacing: item.letterSpacing ? `${item.letterSpacing}px` : undefined,
    textTransform: item.textTransform || undefined,
    textDecoration: 'none',
    borderRadius: cfg.itemBorderRadius || 4,
    cursor: item.disabled ? 'default' : 'pointer',
    opacity: item.disabled ? 0.5 : 1,
    position: 'relative',
    whiteSpace: 'nowrap',
    transition: `color ${transMs}ms, background ${transMs}ms`,
    userSelect: 'none',
    ...indicatorStyle,
  };

  const handleMouseEnter = (e) => {
    if (!isActive && cfg.itemHoverBgColor) {
      e.currentTarget.style.background = cfg.itemHoverBgColor;
    }
    if (hasChildren && cfg.dropdownTrigger === 'hover') {
      setOpenDropdownId(item.itemId);
    }
  };

  const handleMouseLeave = (e) => {
    if (!isActive && cfg.itemHoverBgColor) {
      e.currentTarget.style.background = 'transparent';
    }
    if (hasChildren && cfg.dropdownTrigger === 'hover') {
      setOpenDropdownId(null);
    }
  };

  return (
    <div style={{ position: 'relative' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div style={itemStyle} className={isActive && cfg.activeClass ? cfg.activeClass : undefined} onClick={e => {
        if (hasChildren && cfg.dropdownTrigger === 'click') {
          e.preventDefault();
          setOpenDropdownId(isOpen ? null : item.itemId);
        } else {
          onItemClick(item, e);
        }
      }}>
        {item.label}
        {item.badge && (
          <span style={{
            fontSize: 9, background: item.badgeColor || '#ef4444', color: '#fff',
            borderRadius: 10, padding: '1px 5px', fontWeight: 700, marginLeft: 2,
          }}>
            {item.badge}
          </span>
        )}
        {hasChildren && (
          <span style={{ fontSize: 10, opacity: 0.5, marginLeft: 1 }}>▾</span>
        )}
        {cfg.activeIndicator === 'dot' && isActive && (
          <span style={{
            position: 'absolute', bottom: 2, left: '50%', transform: 'translateX(-50%)',
            width: 4, height: 4, borderRadius: '50%', background: activePrimary,
          }} />
        )}
      </div>

      {hasChildren && isOpen && !isMobile && (
        <DropdownMenu item={item} cfg={cfg} onItemClick={onItemClick} activeId={null} />
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function NavMenuSection({ config, theme, appBranding }) {
  const cfg = { ...D, ...(config || {}) };
  const items = (cfg.items || []).filter(it => !isDisabledAuthLink(it.navType, it.url));

  const [mobileOpen, setMobileOpen]     = useState(false);
  const [activeId, setActiveId]         = useState(null);
  const [isScrolled, setIsScrolled]     = useState(false);
  const [isHidden, setIsHidden]         = useState(false);
  const [isMobile, setIsMobile]         = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  const navigate = useNavigate();
  const lastScrollY = useRef(0);
  const primary = (theme && theme.primary) || 'var(--primary-color)';

  // ── Responsive breakpoint ──────────────────────────────────────────────────
  useEffect(() => {
    const bp = cfg.mobileBreakpoint || 768;
    const check = () => setIsMobile(window.innerWidth < bp);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [cfg.mobileBreakpoint]);

  // ── Scroll effects ──────────────────────────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setIsScrolled(y > (cfg.scrolledTriggerOffset || 50));
      if (cfg.hideOnScroll) {
        const threshold = cfg.hideOnScrollThreshold || 100;
        if (y > threshold) {
          setIsHidden(y > lastScrollY.current);
        } else {
          setIsHidden(false);
        }
      }
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [cfg.hideOnScroll, cfg.hideOnScrollThreshold, cfg.scrolledTriggerOffset]);

  // ── Scroll spy ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!cfg.scrollSpyEnabled) return;
    const spyItems = items.filter(it => it.navType === 'section' && it.highlightWhenActive && it.targetSectionId);
    if (!spyItems.length) return;

    const observers = [];
    spyItems.forEach(item => {
      const el = document.querySelector(`[data-section-id="${item.targetSectionId}"]`);
      if (!el) return;
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              setActiveId(item.itemId);
              if (cfg.hashUpdateOnScroll) {
                try { window.history.replaceState(null, '', `#${item.targetSectionId}`); } catch (_) {}
              }
            }
          });
        },
        {
          rootMargin: `-${cfg.scrollSpyOffset || 80}px 0px -40% 0px`,
          threshold: cfg.scrollSpyThreshold || 0.3,
        }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.scrollSpyEnabled, cfg.scrollSpyOffset, cfg.scrollSpyThreshold, cfg.hashUpdateOnScroll, items.length]);

  // ── Item click handler ──────────────────────────────────────────────────────
  const handleItemClick = useCallback((item, e) => {
    e.preventDefault();
    if (item.disabled) return;

    if (item.navType === 'section' && item.targetSectionId) {
      const el = document.querySelector(`[data-section-id="${item.targetSectionId}"]`);
      if (el) {
        const offset = item.scrollOffset ?? cfg.scrollSpyOffset ?? 80;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        if (item.scrollBehavior === 'instant') {
          window.scrollTo({ top, behavior: 'instant' });
        } else {
          smoothScrollTo(top, cfg.smoothScrollDuration || 600, cfg.scrollEasing || 'ease-in-out');
        }
        setActiveId(item.itemId);
        if (cfg.hashUpdateOnScroll) {
          try { window.history.replaceState(null, '', `#${item.targetSectionId}`); } catch (_) {}
        }
      }
      if (cfg.collapseOnItemClick) setMobileOpen(false);
      setOpenDropdownId(null);

    } else if (item.navType === 'page') {
      const slug = item.targetPageSlug || String(item.targetPageId || '');
      if (!slug) return;
      let href = pageHref(slug);
      if (item.passQueryParams && (item.queryParams || []).length > 0) {
        const qs = item.queryParams.filter(p => p.key).map(p => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value)}`).join('&');
        href = `${href}?${qs}`;
      }
      if (item.openInNewTab) window.open(href, '_blank', 'noopener');
      else navigate(href);

    } else if (item.navType === 'url' && item.url) {
      if (item.openInNewTab) window.open(item.url, '_blank', 'noopener');
      else window.location.href = item.url;

    } else if (item.navType === 'none') {
      setOpenDropdownId(d => d === item.itemId ? null : item.itemId);
    }
  }, [cfg, navigate]);

  const handleLogoClick = useCallback((e) => {
    e.preventDefault();
    const navType = cfg.logoLinkNavType;
    const target = cfg.logoLinkTarget;
    if (!target) return;
    if (navType === 'section') {
      const el = document.querySelector(`[data-section-id="${target}"]`);
      if (el) smoothScrollTo(el.getBoundingClientRect().top + window.scrollY - (cfg.scrollSpyOffset || 80), cfg.smoothScrollDuration || 600, cfg.scrollEasing || 'ease-in-out');
    } else {
      const href = typeof target === 'string' ? target : '/';
      window.location.href = href;
    }
  }, [cfg.logoLinkNavType, cfg.logoLinkTarget, cfg.scrollSpyOffset, cfg.smoothScrollDuration, cfg.scrollEasing]);

  // ── Nav bar background ──────────────────────────────────────────────────────
  const bgColor = isScrolled
    ? `rgba(${hexToRgb(cfg.scrolledBgColor || '#ffffff')},${(cfg.scrolledBgOpacity ?? 95) / 100})`
    : `rgba(${hexToRgb(cfg.navBgColor || '#ffffff')},${(cfg.navBgOpacity ?? 100) / 100})`;

  // ── Position styles ─────────────────────────────────────────────────────────
  const positionStyle = (() => {
    const pos = cfg.menuPosition || 'sticky-top';
    const hidden = cfg.hideOnScroll && isHidden;
    const base = {
      transition: `transform 0.3s ease, background 0.3s ease, box-shadow 0.3s ease`,
      transform: hidden ? 'translateY(-110%)' : 'translateY(0)',
      zIndex: 1030,
    };
    if (pos === 'fixed-top')    return { ...base, position: 'fixed', top: 0, left: 0, right: 0 };
    if (pos === 'sticky-top')   return { ...base, position: 'sticky', top: cfg.stickyOffset || 0 };
    if (pos === 'fixed-bottom') return { ...base, position: 'fixed', bottom: 0, left: 0, right: 0 };
    if (pos === 'sidebar-left') return { ...base, position: 'fixed', top: 0, left: 0, width: cfg.mobileMenuWidth || 280, height: '100vh' };
    if (pos === 'sidebar-right')return { ...base, position: 'fixed', top: 0, right: 0, width: cfg.mobileMenuWidth || 280, height: '100vh' };
    return { ...base, position: 'relative' }; // static
  })();

  // ── Nav bar container ───────────────────────────────────────────────────────
  const navBarStyle = {
    ...positionStyle,
    width: '100%',
    background: bgColor,
    backdropFilter: cfg.navBgBlur ? `blur(${cfg.navBgBlurStrength || 8}px)` : 'none',
    WebkitBackdropFilter: cfg.navBgBlur ? `blur(${cfg.navBgBlurStrength || 8}px)` : 'none',
    borderBottom: cfg.navBorderBottom ? `${cfg.navBorderWidth || 1}px solid ${cfg.navBorderColor || '#e2e8f0'}` : 'none',
    boxShadow: isScrolled ? shadowStyle('md') : shadowStyle(cfg.navShadow || 'sm', cfg.navShadowColor),
  };

  // ── Inner layout ────────────────────────────────────────────────────────────
  // A hamburger with nothing behind it is a button that does nothing: it opens
  // an empty drawer and reads as broken. A header with no menu items still has
  // its brand and CTA, so the right answer is to draw no toggle at all.
  const hasMenu = items.length > 0;
  const showHamburger = hasMenu && (isMobile || cfg.menuLayout === 'hamburger');

  const alignItems = (() => {
    const a = cfg.alignment || 'right';
    if (a === 'left')          return 'flex-start';
    if (a === 'center')        return 'center';
    if (a === 'right')         return 'flex-end';
    if (a === 'space-between') return 'space-between';
    if (a === 'space-around')  return 'space-around';
    return 'flex-end';
  })();

  const itemsContainerStyle = {
    display: 'flex',
    flexDirection: cfg.menuLayout === 'vertical' ? 'column' : 'row',
    alignItems: cfg.menuLayout === 'vertical' ? 'flex-start' : 'center',
    gap: cfg.itemSpacing || 8,
    flex: cfg.alignment === 'space-between' ? 1 : 'none',
    justifyContent: alignItems,
    flexWrap: 'wrap',
  };

  // ── CTA buttons ─────────────────────────────────────────────────────────────
  const renderCTA = () => {
    // New multi-button array takes precedence; fall back to legacy single-button fields
    const buttons = (cfg.navCtaButtons || [])
      .filter(b => b.visible !== false)
      .filter(b => !isDisabledAuthLink(b.navType, b.target));
    const legacyVisible = cfg.navCtaVisible && buttons.length === 0
      && !isDisabledAuthLink(cfg.navCtaNavType, cfg.navCtaTarget);

    const makeStyle = (btn) => {
      const color = btn.color || primary;
      return {
        padding: ctaPadding(btn.size || 'md'),
        fontSize: cfg.fontSize || 14,
        fontWeight: 600,
        borderRadius: btn.borderRadius ?? 6,
        cursor: 'pointer',
        border: btn.style !== 'ghost' ? `1.5px solid ${color}` : 'none',
        background: btn.style === 'filled' ? color : 'transparent',
        color: btn.style === 'filled' ? (btn.textColor || '#fff') : color,
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
      };
    };

    const handleClick = (btn) => (e) => {
      e.preventDefault();
      if (btn.navType === 'section') {
        const el = document.querySelector(`[data-section-id="${btn.target}"]`);
        if (el) smoothScrollTo(el.getBoundingClientRect().top + window.scrollY - (cfg.scrollSpyOffset || 80), cfg.smoothScrollDuration || 600, cfg.scrollEasing || 'ease-in-out');
      } else {
        const href = btn.target || '#';
        if (btn.openInNewTab) window.open(href, '_blank', 'noopener');
        else window.location.href = href;
      }
    };

    if (buttons.length > 0) {
      return (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          {buttons.map((btn) => (
            <a key={btn.id} href={btn.target || '#'} style={makeStyle(btn)} onClick={handleClick(btn)}>
              {btn.label || 'Get Started'}
            </a>
          ))}
        </div>
      );
    }

    // Legacy single-button fallback
    if (legacyVisible) {
      const color = cfg.navCtaColor || primary;
      const legacyBtn = {
        id: 'legacy', color: cfg.navCtaColor, textColor: cfg.navCtaTextColor,
        style: cfg.navCtaStyle, size: cfg.navCtaSize, borderRadius: cfg.navCtaBorderRadius,
        navType: cfg.navCtaNavType, target: cfg.navCtaTarget, openInNewTab: cfg.navCtaOpenNewTab,
        label: cfg.navCtaLabel,
      };
      return <a href={cfg.navCtaTarget || '#'} style={makeStyle(legacyBtn)} onClick={handleClick(legacyBtn)}>{cfg.navCtaLabel || 'Get Started'}</a>;
    }

    return null;
  };

  // ── Logo ────────────────────────────────────────────────────────────────────
  const renderLogo = () => {
    if (!cfg.logoVisible) return null;

    const useDefault = cfg.logoUseAppDefault !== false;
    const imageUrl   = useDefault ? (appBranding?.logo    || cfg.logoImageUrl || '') : (cfg.logoImageUrl || '');
    const labelText  = useDefault ? (appBranding?.app_name || cfg.logoText    || '') : (cfg.logoText    || '');
    // Auto-detect type when using defaults; otherwise honour explicit cfg.logoType
    const logoType   = useDefault
      ? (imageUrl && labelText ? 'both' : imageUrl ? 'image' : 'text')
      : cfg.logoType;

    const logoColor = cfg.logoColor || primary;
    return (
      <a href={cfg.logoLinkTarget || '/'} onClick={handleLogoClick}
        style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {(logoType === 'image' || logoType === 'both') && imageUrl && (
          <img src={imageUrl} alt={labelText || 'Logo'}
            style={{ height: cfg.logoHeight || 40, width: 'auto', maxWidth: cfg.logoWidth || 120, objectFit: 'contain', marginRight: logoType === 'both' ? 8 : 0 }} />
        )}
        {(logoType === 'text' || logoType === 'both') && labelText && (
          <span style={{
            fontFamily: cfg.fontFamily || 'inherit',
            fontSize: cfg.logoFontSize || 20,
            fontWeight: cfg.logoFontWeight || 700,
            color: logoColor,
            lineHeight: 1,
          }}>
            {labelText}
          </span>
        )}
      </a>
    );
  };

  // ── Desktop items ───────────────────────────────────────────────────────────
  const renderDesktopItems = () => (
    <div style={itemsContainerStyle}>
      {items.map(item => {
        const isActive = activeId === item.itemId || item.isActive;
        return (
          <NavItem key={item.itemId} item={item} cfg={cfg} isActive={isActive}
            openDropdownId={openDropdownId} setOpenDropdownId={setOpenDropdownId}
            onItemClick={handleItemClick} isMobile={false} />
        );
      })}
    </div>
  );

  // ── Mobile drawer / overlay ─────────────────────────────────────────────────
  const renderMobileMenu = () => {
    if (!mobileOpen) return null;
    const isDropdown = cfg.mobileMenuStyle === 'hamburger-dropdown';

    // Inherit from nav config — single source of truth
    const bgColor   = `rgba(${hexToRgb(cfg.navBgColor || '#ffffff')},${(cfg.navBgOpacity ?? 100) / 100})`;
    const textColor = cfg.labelColor || '#1e293b';
    const activeColor = cfg.activeIndicatorColor || primary;

    const overlayStyle = {
      position: 'fixed', inset: 0,
      background: isDropdown ? 'transparent' : 'rgba(0,0,0,0.4)',
      zIndex: 1040,
    };

    const panelStyle = isDropdown ? {
      position: 'fixed',
      top: cfg.navHeight || 64,
      left: 0, right: 0,
      background: bgColor,
      zIndex: 1041,
      padding: '8px 24px 16px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
      maxHeight: '70vh', overflowY: 'auto',
    } : {
      position: 'fixed', top: 0, bottom: 0, right: 0,
      width: Math.min(280, window.innerWidth),
      background: bgColor,
      display: 'flex', flexDirection: 'column',
      padding: '80px 24px 24px',
      zIndex: 1041, overflowY: 'auto',
      boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
    };

    return (
      <>
        <div style={overlayStyle} onClick={() => setMobileOpen(false)} />
        <div style={panelStyle}>

          {/* Close button */}
          {!isDropdown && (
            <button onClick={() => setMobileOpen(false)} style={{
              position: 'absolute', top: 20, right: 20,
              background: 'none', border: 'none', cursor: 'pointer',
              color: textColor, fontSize: 28, lineHeight: 1,
            }}>×</button>
          )}

          {/* Items — inherit all typography and color from nav config */}
          {items.map(item => {
            const isActive = activeId === item.itemId || item.isActive;
            const baseWeight = item.fontWeight || cfg.fontWeight || 500;
            return (
              <div key={item.itemId}>
                <div onClick={e => handleItemClick(item, e)} style={{
                  fontSize: item.fontSize || cfg.fontSize || 14,
                  fontWeight: isActive ? Math.max(baseWeight, 600) : baseWeight,
                  fontFamily: item.fontFamily || cfg.fontFamily || 'inherit',
                  color: isActive ? activeColor : (item.labelColor || textColor),
                  letterSpacing: item.letterSpacing ? `${item.letterSpacing}px` : undefined,
                  textTransform: item.textTransform || undefined,
                  cursor: item.disabled ? 'default' : 'pointer',
                  opacity: item.disabled ? 0.5 : 1,
                  padding: isDropdown ? '8px 0' : '10px 0',
                  borderBottom: isDropdown ? 'none' : `1px solid rgba(128,128,128,0.15)`,
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  {item.label}
                  {item.badge && (
                    <span style={{ fontSize: 10, background: item.badgeColor || '#ef4444', color: '#fff', borderRadius: 8, padding: '1px 6px', fontWeight: 700 }}>
                      {item.badge}
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {/* CTA — same buttons as desktop */}
          {((cfg.navCtaButtons || []).some(b => b.visible !== false && !isDisabledAuthLink(b.navType, b.target))
            || (cfg.navCtaVisible && !isDisabledAuthLink(cfg.navCtaNavType, cfg.navCtaTarget))) && (
            <div style={{ marginTop: 24 }}>
              {renderCTA()}
            </div>
          )}
        </div>
      </>
    );
  };

  // ── Hamburger icon ──────────────────────────────────────────────────────────
  const renderHamburger = () => (
    <button onClick={() => setMobileOpen(o => !o)} style={{
      background: 'none', border: 'none', cursor: 'pointer',
      display: 'flex', flexDirection: 'column', gap: 5,
      padding: 4,
    }} aria-label="Open menu">
      {[0, 1, 2].map(i => (
        <span key={i} style={{
          width: 24, height: 2,
          background: cfg.labelColor || '#1e293b',
          borderRadius: 1, display: 'block',
          transition: '0.2s',
          transformOrigin: 'center',
          transform: mobileOpen && i === 0 ? 'rotate(45deg) translate(5px, 5px)'
                   : mobileOpen && i === 1 ? 'scaleX(0)'
                   : mobileOpen && i === 2 ? 'rotate(-45deg) translate(5px, -5px)'
                   : 'none',
          opacity: mobileOpen && i === 1 ? 0 : 1,
        }} />
      ))}
    </button>
  );

  // ── Tabs / pills layout ─────────────────────────────────────────────────────
  const renderTabsOrPills = () => {
    const isPills = cfg.menuLayout === 'pills';
    return (
      <div style={{ display: 'flex', gap: 4 }}>
        {items.map(item => {
          const isActive = activeId === item.itemId || item.isActive;
          return (
            <div key={item.itemId} onClick={e => handleItemClick(item, e)} style={{
              padding: `${cfg.itemPaddingY || 8}px ${cfg.itemPaddingX || 14}px`,
              fontSize: item.fontSize || cfg.fontSize || 14,
              fontWeight: isActive ? 700 : (item.fontWeight || cfg.fontWeight || 500),
              fontFamily: item.fontFamily || cfg.fontFamily || 'inherit',
              color: isActive ? '#fff' : (item.labelColor || cfg.labelColor || '#1e293b'),
              letterSpacing: item.letterSpacing ? `${item.letterSpacing}px` : undefined,
              textTransform: item.textTransform || undefined,
              background: isActive ? (cfg.activeIndicatorColor || primary) : 'transparent',
              borderRadius: isPills ? 999 : (cfg.itemBorderRadius || 6),
              border: !isPills && !isActive ? `1px solid ${cfg.navBorderColor || '#e2e8f0'}` : '1px solid transparent',
              cursor: item.disabled ? 'default' : 'pointer',
              transition: `all ${cfg.transitionDuration || 200}ms`,
            }}>
              {item.label}
            </div>
          );
        })}
      </div>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const isTabsOrPills = cfg.menuLayout === 'tabs' || cfg.menuLayout === 'pills';

  return (
    <>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }
        ${cfg.customCss || ''}
      `}</style>

      <nav style={navBarStyle} role={cfg.role || 'navigation'} aria-label={cfg.ariaLabel || 'Main navigation'} id={cfg.id || undefined}>
        <div style={{
          maxWidth: '100%',
          margin: '0 auto',
          padding: `0 ${cfg.navPaddingX || 24}px`,
          height: cfg.navHeight || 64,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          justifyContent: cfg.alignment === 'space-between' ? 'space-between' : 'flex-start',
        }}>
          {/* Logo */}
          {renderLogo()}

          {/* Items — desktop */}
          {!showHamburger && !isTabsOrPills && (
            <div style={{
              flex: 1, display: 'flex', justifyContent: alignItems, alignItems: 'center',
            }}>
              {renderDesktopItems()}
            </div>
          )}

          {/* Tabs / Pills */}
          {!showHamburger && isTabsOrPills && (
            <div style={{ flex: 1, display: 'flex', justifyContent: alignItems }}>
              {renderTabsOrPills()}
            </div>
          )}

          {/* Spacer when logo + items both on left */}
          {!showHamburger && cfg.alignment !== 'space-between' && <div style={{ flex: 1 }} />}

          {/* CTA — desktop */}
          {!showHamburger && renderCTA()}

          {/* Hamburger toggle */}
          {showHamburger && (
            <>
              <div style={{ flex: 1 }} />
              {renderHamburger()}
            </>
          )}
        </div>
      </nav>

      {/* Mobile menu */}
      {showHamburger && renderMobileMenu()}
    </>
  );
}

// ── Hex → R,G,B string helper ─────────────────────────────────────────────────
function hexToRgb(hex) {
  const h = (hex || '#ffffff').replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(full, 16);
  if (isNaN(n)) return '255,255,255';
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}
